import { BIO_SCROLL_ROOT_SELECTOR, linkNodeSelector } from './linkNodePath';

interface Options {
  behavior?: ScrollBehavior;
  /** How long to keep waiting for a not-yet-rendered target before giving up. */
  timeoutMs?: number;
  /**
   * Called if the target never appears within `timeoutMs` (e.g. it lives on an
   * unloaded infinite-scroll page). Not called on a successful align or on an
   * external cancel.
   */
  onTimeout?: () => void;
}

const DEFAULT_TIMEOUT_MS = 4000;
/**
 * After the first alignment, keep re-aligning as content above the target
 * streams in (racks/cards paging in) and shifts it — for this bounded window —
 * so the jump lands on the section instead of ending up part way down the
 * viewport.
 */
const SETTLE_MS = 1200;

/**
 * Scroll the bio section marked with `data-bio-link="<path>"` into view, scoped
 * to the `[data-bio-scroll-root]` container.
 *
 * The target may not be in the DOM yet — a content-tab body streams in after a
 * cross-tab navigation, and async sections resolve behind Suspense — so we watch
 * the scroll root with a `MutationObserver` and align as soon as it appears (up
 * to `timeoutMs`). Once aligned we keep re-aligning (instantly) through a short
 * settle window, since content streaming in ABOVE the target would otherwise
 * push it out of position after the initial scroll. Returns a cancel function;
 * call it to abandon a pending wait/settle (e.g. on unmount or when a newer
 * target supersedes it).
 */
export function scrollToLinkNode(path: string, options?: Options): () => void {
  const behavior = options?.behavior ?? 'smooth';
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const selector = linkNodeSelector(path);
  const root =
    document.querySelector(BIO_SCROLL_ROOT_SELECTOR) ?? document.body;

  let disposed = false;
  let aligned = false;
  const disposers = new Set<() => void>();

  const dispose = () => {
    if (disposed) {
      return;
    }
    disposed = true;
    disposers.forEach(fn => fn());
    disposers.clear();
  };

  const beginSettle = (element: HTMLElement) => {
    const observer = new MutationObserver(() => {
      if (!disposed) {
        element.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    });
    observer.observe(root, { childList: true, subtree: true });
    const timer = window.setTimeout(dispose, SETTLE_MS);
    disposers.add(() => {
      observer.disconnect();
      window.clearTimeout(timer);
    });
  };

  const alignTo = (element: HTMLElement) => {
    if (aligned) {
      return;
    }
    aligned = true;
    element.scrollIntoView({ behavior, block: 'start' });
    beginSettle(element);
  };

  const existing = document.querySelector<HTMLElement>(selector);
  if (existing) {
    alignTo(existing);
    return dispose;
  }

  const timer = window.setTimeout(() => {
    if (!aligned) {
      options?.onTimeout?.();
    }
    dispose();
  }, timeoutMs);
  const observer = new MutationObserver(() => {
    const element = document.querySelector<HTMLElement>(selector);
    if (element) {
      alignTo(element);
    }
  });
  observer.observe(root, { childList: true, subtree: true });
  disposers.add(() => {
    observer.disconnect();
    window.clearTimeout(timer);
  });

  return dispose;
}
